import { Link } from "react-router-dom";
import { Seo } from "@/components/site/Seo";
import { LegalHero, LegalBody, LegalCta, P, UL, Note } from "@/components/site/Legal";
import { IMAGES, CONTACT } from "@/lib/data";

const LAST_UPDATED = "11 August 2026";

const SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    body: (
      <>
        <P>
          Twins Media ("Twins Media", "we", "us" or "our") is an outdoor and railway station advertising
          business operating from Manipal, Udupi, Karnataka, India. We respect the privacy of everyone who
          visits {CONTACT.website}, enquires about our services or works with us on an advertising campaign.
        </P>
        <P>
          This Privacy Policy explains what personal information we collect, why we collect it, how we use and
          protect it, and the choices available to you. It applies to our website, our enquiry forms, and the
          business communication we exchange with clients and prospective clients.
        </P>
        <P>
          By using our website or submitting an enquiry to us, you confirm that you have read and understood
          this Privacy Policy. If you do not agree with it, please do not use the website or share your
          information with us.
        </P>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: (
      <>
        <P>
          We collect only the information we need to respond to enquiries and deliver advertising services. We
          do not sell personal data, and we do not ask for sensitive personal information on this website.
        </P>
        <p className="text-charcoal font-medium">Information you give us</p>
        <UL
          items={[
            "Your full name and company or organisation name.",
            "Your mobile number and email address.",
            "The service you are interested in and your preferred advertising location or city.",
            "Any details you include in the message field of the enquiry form, or in emails, calls and WhatsApp messages.",
            "Campaign material you share with us, such as artwork, logos, brand guidelines and billing details, when you engage us for a campaign.",
          ]}
        />
        <p className="text-charcoal font-medium">Information collected automatically</p>
        <UL
          items={[
            "Basic technical data such as browser type, device type, operating system and screen size.",
            "Approximate location derived from your IP address (city or region level, not precise location).",
            "Pages viewed, time spent on the site and the referring website, used only to understand which services visitors are interested in.",
            "Standard server logs maintained by our hosting provider for security and reliability.",
          ]}
        />
        <Note>
          We do not knowingly collect payment card details through this website. Any invoicing or payment for
          advertising campaigns is handled separately through agreed banking channels.
        </Note>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    body: (
      <>
        <P>Your information is used for clear, limited business purposes:</P>
        <UL
          items={[
            "To respond to your enquiry, share availability and prepare a quotation.",
            "To plan, book, execute and monitor your advertising campaign across railway stations, digital screens, hoardings and transit media.",
            "To coordinate with railway authorities, site owners, printers and installation partners where required for your campaign.",
            "To send campaign updates, installation photographs, reports and invoices.",
            "To maintain our business records and comply with tax, accounting and legal obligations in India.",
            "To improve our website, our service offering and the quality of our customer support.",
            "To contact you about our advertising services where you have enquired with us or given consent, with the option to opt out at any time.",
          ]}
        />
        <P>
          We do not use your personal information for automated decision making or profiling, and we do not use
          it for any purpose that is unrelated to the reason you shared it with us.
        </P>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "Consent and Legal Basis",
    body: (
      <>
        <P>
          We process personal data in accordance with the Digital Personal Data Protection Act, 2023 and other
          applicable Indian laws, including the Information Technology Act, 2000 and rules made under it.
        </P>
        <UL
          items={[
            "Consent — when you voluntarily submit an enquiry form, email us, call us or message us on WhatsApp, you consent to our using those details to respond and to follow up on your requirement.",
            "Contractual necessity — where we need your information to prepare a proposal, execute a booked campaign or raise an invoice.",
            "Legal obligation — where retention or disclosure is required under tax, accounting or regulatory requirements.",
            "Legitimate business interest — for the security of our website, prevention of misuse, and maintenance of our own business records.",
          ]}
        />
        <P>
          You may withdraw your consent at any time by writing to us at {CONTACT.email}. Withdrawal will not
          affect processing already carried out, and may mean we are unable to continue servicing an active
          campaign enquiry.
        </P>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and Similar Technologies",
    body: (
      <>
        <P>
          Our website uses a minimal number of cookies and similar technologies. These are small files stored by
          your browser that help the site function correctly and help us understand general usage patterns.
        </P>
        <UL
          items={[
            "Essential cookies — required for the website to load, display correctly and remain secure.",
            "Preference storage — remembers basic settings so your experience stays consistent between pages.",
            "Analytics — aggregated, non-identifying statistics about page visits, used to improve content and layout.",
            "Third-party cookies — may be set by embedded services such as Google Maps when those elements load on the Contact page.",
          ]}
        />
        <P>
          You can block or delete cookies through your browser settings at any time. Please note that disabling
          essential cookies may affect how parts of the website behave.
        </P>
      </>
    ),
  },
  {
    id: "third-party",
    title: "Third-Party Services and Embedded Content",
    body: (
      <>
        <P>
          Some parts of our website rely on trusted third-party services. When these load, the provider may
          receive your IP address and standard technical information, governed by their own privacy policies.
        </P>
        <UL
          items={[
            "Google Maps — the map embedded on our Contact page, used to help you locate our Manipal office.",
            "Google Fonts — used to load the typefaces that give the website its appearance.",
            "WhatsApp — if you use the chat button, your message and mobile number are handled by WhatsApp under its own privacy terms.",
            "Website hosting and email providers — used to serve this website and to receive and store enquiry emails.",
          ]}
        />
        <P>
          We do not control how these providers process data on their own platforms, and we recommend reviewing
          their privacy policies if you would like more detail.
        </P>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Sharing and Disclosure",
    body: (
      <>
        <P>
          We do not sell, rent or trade your personal information. We share it only where it is necessary and
          only to the extent required:
        </P>
        <UL
          items={[
            "With railway authorities, municipal bodies, site owners and licensors, where details are required to obtain permissions or confirm a display location.",
            "With printers, fabricators, installation teams and monitoring staff engaged to execute your campaign.",
            "With our accountants, auditors and professional advisers, for invoicing, taxation and compliance.",
            "With our service providers for hosting, email and website maintenance, who act on our instructions.",
            "With government authorities, courts or law enforcement, where disclosure is required by law or to protect our legal rights.",
          ]}
        />
        <P>
          Everyone we share information with is expected to keep it confidential and to use it solely for the
          purpose for which it was shared.
        </P>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data Retention",
    body: (
      <>
        <P>
          We retain personal information only for as long as it serves the purpose it was collected for, or for
          as long as required by law.
        </P>
        <UL
          items={[
            "Enquiries that do not lead to a booking are generally retained for up to 24 months, so that we can respond if you return to us.",
            "Client and campaign records, including artwork, contracts and invoices, are retained for the period required under applicable tax and accounting rules.",
            "Website log and analytics data is retained in aggregated form for a limited period.",
          ]}
        />
        <P>
          When information is no longer needed, we delete it or render it anonymous through secure means.
        </P>
      </>
    ),
  },
  {
    id: "security",
    title: "Data Security",
    body: (
      <>
        <P>
          We take reasonable technical and organisational measures to protect the information you share with us,
          including secure transmission over HTTPS, restricted access to enquiry records, password-protected
          accounts and devices, and limiting access to staff who need the information to do their work.
        </P>
        <P>
          However, no method of transmission over the internet or method of electronic storage is completely
          secure. While we work to protect your information, we cannot guarantee absolute security, and you
          share information with us on that understanding.
        </P>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your Rights and Choices",
    body: (
      <>
        <P>
          Subject to applicable law, you have the following rights in relation to the personal data we hold
          about you:
        </P>
        <UL
          items={[
            "Access — request a summary of the personal data we hold about you and how it is being processed.",
            "Correction — ask us to correct information that is inaccurate, incomplete or out of date.",
            "Erasure — ask us to delete your personal data where it is no longer required and where we are not obliged to retain it.",
            "Withdraw consent — tell us to stop using your data for enquiry follow-ups or communication at any time.",
            "Grievance redressal — raise a concern with us about how your data has been handled and receive a response.",
            "Nomination — nominate another individual to exercise your rights in the event of death or incapacity, as provided under the Digital Personal Data Protection Act, 2023.",
          ]}
        />
        <P>
          To exercise any of these rights, write to us at {CONTACT.email} or call {CONTACT.phone}. We will
          verify your identity before acting on a request and will respond within a reasonable timeframe.
        </P>
      </>
    ),
  },
  {
    id: "client-material",
    title: "Client Artwork and Campaign Material",
    body: (
      <>
        <P>
          When you engage us for a campaign, you may share creative artwork, logos, product photographs and
          brand guidelines. We treat this material as confidential and use it only to produce, print, install
          and display your advertisement as agreed.
        </P>
        <P>
          We may photograph installed campaigns for verification, reporting and our own portfolio or case
          studies. If you prefer that your campaign photographs are not used for promotional purposes, simply
          write to us and we will honour that request.
        </P>
      </>
    ),
  },
  {
    id: "children",
    title: "Children's Privacy",
    body: (
      <>
        <P>
          Our website and services are intended for businesses and adult professionals. We do not knowingly
          collect personal information from children below the age of 18. If you believe a child has provided
          information to us, please contact us and we will delete it promptly.
        </P>
      </>
    ),
  },
  {
    id: "external-links",
    title: "Links to Other Websites",
    body: (
      <>
        <P>
          Our website may contain links to third-party websites, maps, directories or social media platforms.
          This Privacy Policy does not apply to those sites. We are not responsible for their content or
          privacy practices, and we encourage you to read their policies before sharing information with them.
        </P>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    body: (
      <>
        <P>
          We may update this Privacy Policy from time to time to reflect changes in our practices, our services
          or applicable law. The revised policy will be posted on this page with an updated date at the top.
        </P>
        <P>
          We encourage you to review this page periodically. Continued use of our website or services after an
          update means you accept the revised policy.
        </P>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact and Grievances",
    body: (
      <>
        <P>
          For any question, request or grievance regarding this Privacy Policy or the handling of your personal
          data, please reach out to us:
        </P>
        <div className="bg-paper border border-charcoal/10 p-6 md:p-8 space-y-3">
          <p className="font-display text-2xl text-charcoal">Twins Media</p>
          <address className="not-italic text-charcoal-muted leading-relaxed">
            {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
          </address>
          <p className="text-charcoal-muted">
            Phone — <a href="tel:+919980549123" className="text-maroon hover:text-gold transition-colors">{CONTACT.phone}</a>
          </p>
          <p className="text-charcoal-muted break-all">
            Email — <a href={`mailto:${CONTACT.email}`} className="text-maroon hover:text-gold transition-colors">{CONTACT.email}</a>
          </p>
          <p className="text-charcoal-muted">Business hours — Monday to Saturday, 9:00 AM to 6:00 PM IST</p>
        </div>
        <P>
          Please also read our{" "}
          <Link to="/terms" className="text-maroon underline underline-offset-4 hover:text-gold transition-colors">
            Terms &amp; Conditions
          </Link>
          , which govern the use of this website and our advertising services.
        </P>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy"
        description="How Twins Media collects, uses, shares and protects personal information submitted through our website and advertising enquiries, in line with India's Digital Personal Data Protection Act, 2023."
        path="/privacy-policy"
      />
      <LegalHero
        overline="Legal"
        title={["Privacy", <span key="s" className="italic text-gold">Policy</span>]}
        subtitle="How Twins Media collects, uses and protects the information you share with us — written plainly, so you know exactly where you stand."
        updated={LAST_UPDATED}
        image={IMAGES.transitMedia}
      />
      <LegalBody sections={SECTIONS} />
      <LegalCta heading="Questions about your data? We're happy to answer." cta="Contact Us" />
    </div>
  );
}
